import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Booking, BookingTicketType, Movie, Screening, SeatLayout } from '../types';
import { supabase } from '../lib/supabase';
import { canManageScreening } from '../lib/permissions';
import AuthModal from './AuthModal';
import BookingConfirmation from './BookingConfirmation';
import { SeatSelection, TermsAndServices, Payment } from './booking';
import BookingLayoutInterface from './BookingLayoutInterface';
import { calculateAvailableSeats } from '../lib/seatUtils';

import styles from '../styles/BookingModal.module.css';

// === NEW STATE TYPES FOR REFACTORING ===
type BookingPhase =
  | 'idle'              // Initial state, no selection
  | 'selecting'         // User selecting seats
  | 'pending-checkout'  // Seats selected, ready to checkout
  | 'checkout'          // In payment flow
  | 'processing'        // Payment processing
  | 'completed';        // Booking confirmed

interface SelectionState {
  seatIds: string[];
  screening: Screening | null;
  isOwnPending: boolean;  // Are these from user's pending booking?
  timestamp: number;       // When selection was made
}

interface PendingBookingState {
  bookingId: number | null;
  hasOther: boolean;  // Has other pending bookings?
  expiresAt: string | null;
}
// === END NEW TYPES ===

interface BookingModalProps {
  show: boolean;
  onClose: () => void;
  movie: Movie;
  screenings: Screening[];
  session: any;
  pendingBookingInfo?: {
    screeningId: number;
    bookingId: number;
    seatIds: string[];
  } | null;
}

export default function BookingModal({ show, onClose, movie, screenings, session, pendingBookingInfo }: BookingModalProps) {
  const [selectedScreening, setSelectedScreening] = useState<Screening | null>(null);
  const [localScreenings, setLocalScreenings] = useState<Screening[]>(screenings);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  // Allow empty string so the field can be cleared
  const [seatCounts, setSeatCounts] = useState({ silver: "", gold: "", diamond: "" });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(true);
  const [checkoutInitiated, setCheckoutInitiated] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [phoneNumber1, setPhoneNumber1] = useState('');
  const [phoneNumber2, setPhoneNumber2] = useState('');
  const [phoneNumbersMatch, setPhoneNumbersMatch] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [currentBookings, setCurrentBookings] = useState<Booking[]>([]);
  const [seatsAvailable, setSeatsAvailable] = useState(true);
  const [seatLayout, setSeatLayout] = useState<SeatLayout | null>(null);
  const [bookedSeats, setBookedSeats] = useState<string[]>([]);
  const [heldSeats, setHeldSeats] = useState<string[]>([]);
  const [selectedSeatIds, setSelectedSeatIds] = useState<string[]>([]);
  const [issuedQr, setIssuedQr] = useState<string | null>(null);
  const [generatingQr, setGeneratingQr] = useState(false);

  const [canPhoneBook, setCanPhoneBook] = useState(false);
  const [isPhoneBooking, setIsPhoneBooking] = useState(false);

  const [customerName, setCustomerName] = useState('');
  const [organizerTerms, setOrganizerTerms] = useState('');
  const [commissionPercent, setCommissionPercent] = useState(13);
  const [organizerCanPhoneBook, setOrganizerCanPhoneBook] = useState(true);
  const [hasPendingBooking, setHasPendingBooking] = useState(false);
  const [localBookingId, setLocalBookingId] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // Stores the ticket-type breakdown built at reservation time so it can be
  // attached to the Booking object passed to BookingConfirmation.
  const [pendingTicketDetails, setPendingTicketDetails] = useState<BookingTicketType[] | null>(null);

  // === NEW CONSOLIDATED STATES (Running alongside old states during migration) ===
  const [bookingPhase, setBookingPhase] = useState<BookingPhase>('idle');
  const [selection, setSelection] = useState<SelectionState>({
    seatIds: [],
    screening: null,
    isOwnPending: false,
    timestamp: 0
  });
  const [pendingBooking, setPendingBooking] = useState<PendingBookingState>({
    bookingId: null,
    hasOther: false,
    expiresAt: null
  });
  // === END NEW STATES ===


  // Fetch organizer terms and commission
  useEffect(() => {
    (async () => {
      if (!movie.organizer_id) return;
      const { data } = await supabase
        .from('organizers')
        .select('terms_and_conditions, commission_percent, can_phone_book')
        .eq('id', movie.organizer_id)
        .maybeSingle();

      if (data) {
        if (data.terms_and_conditions) setOrganizerTerms(data.terms_and_conditions);
        if (data.commission_percent !== undefined && data.commission_percent !== null) setCommissionPercent(data.commission_percent);
        if (data.can_phone_book !== undefined && data.can_phone_book !== null) setOrganizerCanPhoneBook(data.can_phone_book);
        else setOrganizerCanPhoneBook(true); // Default to true if not set
      }
    })();
  }, [movie]);

  // Fetch screenings when the modal opens
  useEffect(() => {
    if (show && movie.id) {
      fetchScreenings();
    }
  }, [show, movie.id]);

  // === NEW: Reset phase when modal opens fresh ===
  useEffect(() => {
    if (show && !pendingBookingInfo) {
      // Reset to idle when opening fresh (not continuing pending)
      if (bookingPhase !== 'idle') {
        transitionPhase('idle', 'Modal opened fresh');
      }
    }
  }, [show, pendingBookingInfo]);
  // === END Phase Reset ===


  // Restore pending booking state when modal opens with pending info
  useEffect(() => {
    if (show && pendingBookingInfo && localScreenings.length > 0) {
      // Find and select the screening from the pending booking
      const screening = localScreenings.find(s => s.id === pendingBookingInfo.screeningId);
      if (screening) {
        setSelectedScreening(screening);
        setSelectedLocation(screening.location || '');
        // Set the selected seat IDs from the pending booking
        setSelectedSeatIds(pendingBookingInfo.seatIds);
        setAcceptedTerms(true);
        toast.success('Continuing your pending booking. Please complete payment.');
      }
    }
  }, [show, pendingBookingInfo, localScreenings]);

  // Check if user has any pending bookings
  useEffect(() => {
    const checkPendingBookings = async () => {
      if (!show || !session?.user?.id) {
        setHasPendingBooking(false);
        return;
      }
      const now = new Date().toISOString();

      // Query for pending bookings
      let query = supabase
        .from('bookings')
        .select('id, expires_at')
        .eq('user_id', session.user.id)
        .eq('status', 'pending')
        .gt('expires_at', now);

      // If we're continuing a specific pending booking, exclude it from the warning
      if (pendingBookingInfo?.bookingId) {
        query = query.neq('id', pendingBookingInfo.bookingId);
      }
      // Only exclude the current session's booking if we're actively in checkout
      // If user closed modal and reopened, we WANT to show the warning
      if (localBookingId && checkoutInitiated) {
        query = query.neq('id', localBookingId);
      }

      const { data: pendingBookings } = await query.limit(1);
      console.log('[BookingModal] Pending booking check:', {
        found: pendingBookings?.length || 0,
        bookings: pendingBookings,
        hasPendingBooking: (pendingBookings || []).length > 0
      });
      setHasPendingBooking((pendingBookings || []).length > 0);
    };

    checkPendingBookings();

    // Re-check when the window regains focus (e.g., after closing My Bookings modal)
    const handleFocus = () => {
      if (show) checkPendingBookings();
    };
    window.addEventListener('focus', handleFocus);

    // Re-check when user clicks anywhere (catches modal closes)
    const handleClick = () => {
      // Debounce: only check if modal is visible
      if (show) {
        setTimeout(checkPendingBookings, 300);
      }
    };
    document.addEventListener('click', handleClick);

    // Also check periodically in case the pending booking expires
    const interval = setInterval(checkPendingBookings, 5000);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('click', handleClick);
      clearInterval(interval);
    };
  }, [show, session?.user?.id, pendingBookingInfo?.bookingId, localBookingId, checkoutInitiated]);



  const fetchScreenings = async () => {
    try {
      const { data, error } = await supabase
        .from('screenings')
        .select('*')
        .eq('movie_id', movie.id)
        .gte('start_time', new Date().toISOString())
        .order('start_time', { ascending: true });

      if (error) {
        console.error('Error fetching screenings:', error);
        toast.error('Failed to load screenings');
        return;
      }

      if (data) {
        setLocalScreenings(data);
        const locs = Array.from(new Set(data.map((s: Screening) => s.location)));
        if (locs.length === 1) {
          setSelectedLocation(locs[0]);
        }
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error('Failed to load screenings');
    }
  };

  // Pricing for each seat type — read from DB record first, fall back to movie base price, then hardcoded safe default.
  // Backward-compat: if all tier prices are 0 (default for screenings created before per-tier pricing),
  // treat them as "not configured" and use movie.price instead.
  const splitData = selectedScreening?.seat_splitting;
  const basePrice = (selectedScreening as any)?.price ?? movie.price ?? 10;

  // A stored price of 0 means the organizer never configured it → fall back to basePrice.
  const resolvePrice = (storedPrice: number | null | undefined): number =>
    storedPrice != null && storedPrice > 0 ? storedPrice : basePrice;

  const seatPrices = {
    silver:  resolvePrice(splitData?.silver_price),
    gold:    resolvePrice(splitData?.gold_price),
    diamond: resolvePrice(splitData?.diamond_price),
  };

  // Convert inputs to numbers (fallback to 0 if empty)
  const silverCount = parseInt(seatCounts.silver || "0");
  const goldCount = parseInt(seatCounts.gold || "0");
  const diamondCount = parseInt(seatCounts.diamond || "0");

  const splitEnabled = !!selectedScreening?.enable_seatsplitting;
  const totalTickets = seatLayout ? selectedSeatIds.length : (splitEnabled ? (silverCount + goldCount + diamondCount) : silverCount);

  // Inclusive Pricing Logic
  // User pays the stated ticket price. Fee is deducted from this total.
  // For split bookings, compute the correct mixed-tier total so the displayed
  // amount exactly matches what will be charged at checkout.
  const totalUserPays = seatLayout
    ? selectedSeatIds.length * basePrice
    : splitEnabled
      ? (silverCount * seatPrices.silver + goldCount * seatPrices.gold + diamondCount * seatPrices.diamond)
      : silverCount * basePrice;
  const totalProcessingFee = totalUserPays * (commissionPercent / 100);
  const totalAmount = totalUserPays - totalProcessingFee;
  const totalWithFee = totalUserPays;

  // === NEW: Phase Transition Function ===
  const transitionPhase = (newPhase: BookingPhase, reason?: string) => {
    console.log(`[BookingModal] Phase: ${bookingPhase} → ${newPhase}${reason ? ` (${reason})` : ''}`);
    setBookingPhase(newPhase);

    // Sync with old states for backward compatibility during migration
    switch (newPhase) {
      case 'idle':
        setCheckoutInitiated(false);
        setIsProcessing(false);
        break;
      case 'checkout':
        setCheckoutInitiated(true);
        break;
      case 'processing':
        setIsProcessing(true);
        break;
      case 'completed':
        setShowConfirmation(true);
        setCheckoutInitiated(false);
        setIsProcessing(false);
        break;
    }
  };
  // === END Transition Function ===

  useEffect(() => {
    (async () => {
      const uid = session?.user?.id;
      const sid = selectedScreening?.id;
      if (!uid || !sid) { setCanPhoneBook(false); return; }
      try {
        const allowed = await canManageScreening(uid, sid);
        // Phone booking is allowed only if:
        // 1. User has permission to manage the screening (editor/organizer/admin)
        // 2. The organizer has phone booking enabled
        setCanPhoneBook(!!allowed && organizerCanPhoneBook);
      } catch {
        setCanPhoneBook(false);
      }
    })();
  }, [session?.user?.id, selectedScreening?.id, organizerCanPhoneBook]);

  const isBookingClosed = (() => {
    const now = new Date();
    const startOk = movie.booking_start_time ? now >= new Date(movie.booking_start_time) : true;
    const stopOk = movie.booking_stop_enabled ? (!!movie.booking_stop_time && now < new Date(movie.booking_stop_time)) : true;
    return !movie.accept_booking || !startOk || !stopOk;
  })();

  useEffect(() => {
    setPhoneNumbersMatch(phoneNumber1 === phoneNumber2);
  }, [phoneNumber1, phoneNumber2]);

  // === UPDATED: Screening Change Effect (Now Phase-Aware) ===
  useEffect(() => {
    if (selectedScreening) {
      // Clear selected seats when switching screening ONLY if:
      // 1. Not continuing a pending booking
      // 2. Not in active checkout/processing (use new bookingPhase!)
      const canClearSeats = !pendingBookingInfo &&
        bookingPhase !== 'checkout' &&
        bookingPhase !== 'processing';

      if (canClearSeats) {
        console.log('[BookingModal] Screening changed, clearing seats');
        setSelectedSeatIds([]);
      } else {
        console.log('[BookingModal] Screening changed but NOT clearing seats (phase:', bookingPhase, ')');
      }

      if (selectedScreening.enable_seatsplitting) {
        fetchSeatDetails();
      } else {
        setSeatCounts(prev => ({ ...prev, gold: "", diamond: "" }));
      }
      fetchSeatLayout();

      // Poll for seat updates (expiration of held seats, or new bookings)
      const interval = setInterval(fetchSeatLayout, 30000); // 30 seconds
      return () => clearInterval(interval);
    }
  }, [selectedScreening, bookingPhase, pendingBookingInfo]); // Added bookingPhase dependency
  // === END Updated Effect ===


  useEffect(() => {
    if (selectedScreening?.seat_splitting) {
      checkSeatAvailability();
    }
  }, [seatCounts, selectedScreening]);

  useEffect(() => {
    if (seatLayout) {
      checkSeatAvailability();
    }
  }, [selectedSeatIds, seatLayout]);

  const fetchSeatDetails = async () => {
    if (!selectedScreening) return;

    try {
      const { data, error } = await supabase
        .from('seatssplitting')
        .select('*')
        .eq('screening_id', selectedScreening.id)
        .single();

      if (error) {
        console.error('Error fetching seat details:', error);
        return;
      }

      setSelectedScreening(prev => prev ? { ...prev, seat_splitting: data } : null);
    } catch (error) {
      console.error('Unexpected error fetching seat details:', error);
    }
  };

  const fetchSeatLayout = async () => {
    if (!selectedScreening) return;
    try {
      const { data: layoutRows, error } = await supabase
        .from('seat_layouts')
        .select('*')
        .eq('screening_id', selectedScreening.id)
        .limit(1);
      if (error) return;
      if (layoutRows && layoutRows.length > 0) {
        const layout = layoutRows[0];
        setSeatLayout({ id: layout.id, screening_id: layout.screening_id, layout_name: layout.layout_name, rows: layout.rows, cols: layout.cols, seats: layout.seats });

        const { data: bookedData, error } = await supabase
          .rpc('get_booked_seat_ids_for_screening', { p_screening_id: selectedScreening.id });

        if (error) {
          console.error('Error fetching booked seats:', error);
          setBookedSeats([]);
          setHeldSeats([]);
        } else {
          console.log('[BookingModal] RPC Data:', bookedData);
          console.log('[BookingModal] Pending Info:', pendingBookingInfo);

          // If the RPC hasn't been updated yet, it might return just strings or objects without status.
          // We need to handle both cases gracefully or assume the migration runs.
          // Safest is to treat them as booked if no status, or verify shape.
          // Based on our migration, it returns { seat_id, status }

          const booked: string[] = [];
          const held: string[] = [];

          (bookedData || []).forEach((item: any) => {
            // Handle both old format (if user doesn't migrate) and new format
            const seatId = typeof item === 'string' ? item : item.seat_id;
            const status = typeof item === 'string' ? 'confirmed' : (item.status || 'confirmed');

            console.log(`[BookingModal] Seat ${seatId} status: ${status}`);

            // Exclude seats that belong to:
            // 1. Current user's pending booking (when continuing via pendingBookingInfo)
            // 2. Currently selected seats (during active booking session)
            const isUserOwnPendingSeat = pendingBookingInfo?.seatIds && pendingBookingInfo.seatIds.includes(seatId);
            const isCurrentlySelectedSeat = selectedSeatIds.includes(seatId);

            if (isUserOwnPendingSeat || isCurrentlySelectedSeat) {
              console.log(`[BookingModal] Exclusion: Seat ${seatId} belongs to current user`);
              return;
            }

            if (status === 'confirmed') {
              booked.push(seatId);
            } else {
              held.push(seatId);
            }
          });

          console.log('[BookingModal] Final Booked:', booked);
          console.log('[BookingModal] Final Held:', held);


          setBookedSeats(booked);
          setHeldSeats(held);
        }
        // Don't clear selected seats here - user may have seats selected during checkout
        // Selected seats should only be cleared when:
        // 1. User explicitly closes/cancels the modal
        // 2. User completes booking successfully
        // 3. User switches to a different screening
      } else {
        setSeatLayout(null);
        setBookedSeats([]);
        setHeldSeats([]);
        // Don't clear selected seats here either
      }
    } catch { }
  };

  const checkSeatAvailability = () => {
    if (seatLayout) {
      const conflict = selectedSeatIds.some(id => bookedSeats.includes(id) || heldSeats.includes(id));
      const isAvailable = !conflict;
      setSeatsAvailable(isAvailable);

      // Don't show error if seats are held by user's own pending booking
      // The "You have a pending booking" warning will handle this case
      if (!isAvailable && checkoutInitiated && !hasPendingBooking) {
        setCheckoutInitiated(false);
        toast.error('Selected seats are no longer available. Please adjust your selection.');
      }
      return;
    }
    if (!selectedScreening?.seat_splitting) return;
    const availableSeats = calculateAvailableSeats(selectedScreening.seat_splitting);
    const isAvailable = splitEnabled
      ? silverCount <= availableSeats.silver && goldCount <= availableSeats.gold && diamondCount <= availableSeats.diamond
      : silverCount <= availableSeats.silver;
    setSeatsAvailable(isAvailable);
    if (!isAvailable && checkoutInitiated) {
      setCheckoutInitiated(false);
      toast.error('Selected seats are no longer available. Please adjust your selection.');
    }
  };

  const verifyAvailabilityRealTime = async () => {
    if (!selectedScreening) return false;

    try {
      if (seatLayout) {
        const { data: bookedData } = await supabase
          .rpc('get_booked_seat_ids_for_screening', { p_screening_id: selectedScreening.id });

        const bookedSeatIds = (bookedData || []).map((b: any) => typeof b === 'string' ? b : b.seat_id);
        const bookedSet = new Set<string>(bookedSeatIds);
        const hasConflict = selectedSeatIds.some(id => bookedSet.has(id));

        if (hasConflict) {
          toast.error('Some selected seats have been booked or held. Please reselect.');
          return false;
        }
        return true;
      }
      const { data, error } = await supabase
        .from('seatssplitting')
        .select('*')
        .eq('screening_id', selectedScreening.id)
        .single();

      if (error) {
        console.error('Error verifying seat availability:', error);
        toast.error('Could not verify seat availability. Please try again.');
        return false;
      }

      const availableSeats = calculateAvailableSeats(data);

      if (splitEnabled) {
        if (silverCount > availableSeats.silver) {
          toast.error(`Only ${availableSeats.silver} Silver seats available`);
          return false;
        }
        if (goldCount > availableSeats.gold) {
          toast.error(`Only ${availableSeats.gold} Gold seats available`);
          return false;
        }
        if (diamondCount > availableSeats.diamond) {
          toast.error(`Only ${availableSeats.diamond} Diamond seats available`);
          return false;
        }
      } else {
        if (silverCount > availableSeats.silver) {
          toast.error(`Only ${availableSeats.silver} seats available`);
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Unexpected error verifying availability:', error);
      toast.error('Could not verify seat availability. Please try again.');
      return false;
    }
  };




  const initiateCheckout = async () => {
    console.log('initiateCheckout called');
    console.log('State:', { hasPendingBooking, isBookingClosed, acceptedTerms, session: !!session, selectedScreening: !!selectedScreening, totalSeats: 0 }); // totalSeats calc below

    if (hasPendingBooking) {
      console.log('Blocked: hasPendingBooking');
      toast.error('You have a pending booking. Please complete or cancel it first.');
      return;
    }
    if (isBookingClosed) {
      console.log('Blocked: isBookingClosed');
      toast.error('sorry. the booking is over');
      return;
    }
    if (!acceptedTerms) {
      console.log('Blocked: !acceptedTerms');
      toast.error('You must accept the terms and conditions to proceed');
      return;
    }

    if (!session || !selectedScreening) {
      console.log('Blocked: !session or !selectedScreening');
      toast.error('Please sign in and select a screening');
      return;
    }

    // Prepare booking details for reservation
    const bookingTypes: ('silver' | 'gold' | 'diamond')[] = splitEnabled ? ['silver', 'gold', 'diamond'] : ['silver'];
    let totalSeats = 0;
    let totalAmount = 0;
    let ticketDetails: { type: string; count: number }[] = [];
    // Typed version stored for BookingConfirmation display
    const typedTicketDetails: BookingTicketType[] = [];

    if (seatLayout) {
      totalSeats = selectedSeatIds.length;
      totalAmount = totalWithFee;
      // seat-map booking → no tier breakdown
      setPendingTicketDetails(null);
    } else {
      for (let type of bookingTypes) {
        const count = parseInt(seatCounts[type] || "0");
        if (count > 0) {
          const pricePerTicket = splitEnabled ? seatPrices[type] : movie.price;
          totalSeats += count;
          totalAmount += pricePerTicket * count;
          ticketDetails.push({ type, count });
          typedTicketDetails.push({ ticket_type: type, quantity: count });
        }
      }
      // Store for use when the payment succeeds in handleBooking
      setPendingTicketDetails(splitEnabled && typedTicketDetails.length > 0 ? typedTicketDetails : null);
    }

    console.log('Seats calculated:', totalSeats);

    if (totalSeats < 1) {
      console.log('Blocked: totalSeats < 1');
      toast.error('Please select at least one seat');
      return;
    }

    try {
      // If we are continuing a pending booking, skip reservation
      if (pendingBookingInfo && pendingBookingInfo.bookingId) {
        console.log('Continuing booking:', pendingBookingInfo);
        // Verify we are on the correct screening
        if (selectedScreening.id !== pendingBookingInfo.screeningId) {
          console.log('Blocked: Screening mismatch', selectedScreening.id, pendingBookingInfo.screeningId);
          toast.error('Screening mismatch. Please restart booking.');
          return;
        }

        setLocalBookingId(pendingBookingInfo.bookingId);
        transitionPhase('checkout', 'Resuming pending booking'); // Fixed: Use transition
        toast.success("Resuming your booking...");
        return;
      }

      // Reserve seats (Hold)
      const { data: reservationData, error: reservationError } = await supabase.rpc('reserve_booking', {
        p_screening_id: selectedScreening.id,
        p_user_id: session.user.id,
        p_seats_count: totalSeats,
        p_total_amount: totalAmount,
        p_seat_ids: seatLayout ? selectedSeatIds : null,
        p_ticket_types: seatLayout ? null : ticketDetails,
        p_customer_name: (customerName || (session.user.email || '').split('@')[0]),
        p_phone_number: phoneNumber1,
        p_terms_accepted: acceptedTerms
      });

      if (reservationError) {
        console.error("Error reserving seats:", reservationError);
        toast.error(reservationError.message || "Failed to reserve seats. They may have been taken.");
        transitionPhase('idle', 'Reservation failed'); // NEW: Reset to idle on error
        return;
      }

      if (reservationData && reservationData.id) {
        setLocalBookingId(reservationData.id);
        setHasPendingBooking(true); // Mark that user has a pending booking
        transitionPhase('checkout', 'Seats reserved successfully'); // NEW: Use transition
        toast.success("Seats reserved for 5 minutes");
      }
    } catch (err) {
      console.error("Unexpected error during reservation:", err);
      toast.error("An error occurred while reserving seats.");
      setCheckoutInitiated(false);
    }
  };

  const confirmPhoneBooking = async () => {
    if (!canPhoneBook) {
      toast.error('Only editors, organizers, or admins can confirm phone bookings');
      return;
    }
    if (!acceptedTerms) {
      toast.error('You must accept the terms and conditions to proceed');
      return;
    }
    if (!phoneNumber1 || !phoneNumber2 || !phoneNumbersMatch) {
      toast.error('Please provide and confirm customer phone number');
      return;
    }
    if (!selectedScreening) {
      toast.error('Please select a screening');
      return;
    }
    if (totalTickets < 1) {
      toast.error('Please select at least one seat');
      return;
    }
    if (!customerName.trim()) {
      toast.error('Please enter customer name');
      return;
    }

    // For phone booking, we do reservation and confirmation in one flow (or rely on existing reservation logic if we want to unify)
    // To keep it simple and unified: Reserve first, then Confirm.

    try {
      // 1. Prepare Data
      const bookingTypes: ('silver' | 'gold' | 'diamond')[] = splitEnabled ? ['silver', 'gold', 'diamond'] : ['silver'];
      let totalSeats = 0;
      let totalAmount = 0;
      let ticketDetails: { type: string; count: number }[] = [];

      if (seatLayout) {
        totalSeats = selectedSeatIds.length;
        totalAmount = totalWithFee;
      } else {
        for (let type of bookingTypes) {
          const count = parseInt(seatCounts[type] || "0");
          if (count > 0) {
            // Inclusive Pricing: User pays the ticket price exactly
            const pricePerTicket = splitEnabled ? seatPrices[type] : movie.price;
            totalSeats += count;
            totalAmount += pricePerTicket * count;
            ticketDetails.push({ type, count });
          }
        }
      }

      // 2. Reserve
      const { data: reservationData, error: reservationError } = await supabase.rpc('reserve_booking', {
        p_screening_id: selectedScreening.id,
        p_user_id: session!.user.id,
        p_seats_count: totalSeats,
        p_total_amount: totalAmount,
        p_seat_ids: seatLayout ? selectedSeatIds : null,
        p_ticket_types: seatLayout ? null : ticketDetails,
        p_customer_name: customerName,
        p_phone_number: phoneNumber1,
        p_terms_accepted: acceptedTerms
      });

      if (reservationError) {
        toast.error(reservationError.message || "Failed to reserve seats");
        return;
      }

      const bookingId = reservationData.id;

      // 3. Confirm
      const { data: confirmedData, error: confirmError } = await supabase.rpc('confirm_booking', {
        p_booking_id: bookingId,
        p_payment_intent_id: null,
        p_payment_status: 'phone_booking',
        p_is_phone_booking: true
      });

      if (confirmError) {
        console.error("Error confirming phone booking:", confirmError);
        toast.error("Failed to confirm booking");
        return;
      }

      // 4. Notifications & Cleanup
      const username = (session!.user.email || '').split('@')[0];
      const message = `booking made for ${customerName} by ${username} for €${totalWithFee.toFixed(2)}`;
      try {
        await supabase
          .from('notifications')
          .insert([
            {
              actor_id: session!.user.id,
              actor_email: session!.user.email,
              entity: 'booking',
              entity_id: bookingId,
              action: 'phone booking',
              message,
              details: {
                customer_name: customerName,
                total: totalWithFee,
                booking_id: bookingId,
              }
            }
          ]);
      } catch { }

      // Build typed ticket details from the loop above so confirmation can show tier breakdown
      const phoneTypedDetails: BookingTicketType[] = ticketDetails
        .map(td => ({ ticket_type: td.type as BookingTicketType['ticket_type'], quantity: td.count }));
      const confirmedBooking: Booking = {
        ...(confirmedData as Booking),
        ticket_types: seatLayout ? null : (splitEnabled && phoneTypedDetails.length > 0 ? phoneTypedDetails : null),
      };
      setCurrentBookings([confirmedBooking]);
      toast.success('Phone booking successful');
      setIssuedQr(null)
      setGeneratingQr(true)
      setShowConfirmation(true);
      // Refresh seats
      if (seatLayout) {
        const { data: bookedData } = await supabase
          .rpc('get_booked_seat_ids_for_screening', { p_screening_id: selectedScreening.id });

        const booked: string[] = [];
        const held: string[] = [];
        (bookedData || []).forEach((item: any) => {
          const seatId = typeof item === 'string' ? item : item.seat_id;
          const status = typeof item === 'string' ? 'confirmed' : (item.status || 'confirmed');
          if (status === 'confirmed') booked.push(seatId);
          else held.push(seatId);
        });
        setBookedSeats(booked);
        setHeldSeats(held);
        setSelectedSeatIds([]);
      }

      // Clear all pending booking state after successful phone booking
      transitionPhase('completed', 'Phone booking confirmed'); // NEW
      setLocalBookingId(null);
      setHasPendingBooking(false);

      // Issue QR Code for Phone Booking
      if (bookingId) {
        try {
          const { data } = await supabase.functions.invoke<{ qr: string, warning?: string }>('issue-qr', {
            body: { booking_id: bookingId, email: session?.user.email }
          })
          if (data?.qr) {
            setIssuedQr(data.qr)
          } else {
            const { data: qrRow } = await supabase
              .from('booking_qr_codes')
              .select('iv_base64, cipher_base64')
              .eq('booking_id', bookingId)
              .maybeSingle()
            const iv = (qrRow as { iv_base64?: string } | null)?.iv_base64
            const ct = (qrRow as { cipher_base64?: string } | null)?.cipher_base64
            if (iv && ct) setIssuedQr(`v1:${iv}:${ct}`)
          }
        } catch { }
        finally { setGeneratingQr(false) }
      }

    } catch (error) {
      console.error("Unexpected error:", error);
      toast.error("An unexpected error occurred");
    }
  };

  const handleBooking = async (paymentDetails: any) => {
    setIsProcessing(true);
    // DEBUG: Alert on start
    // window.alert(`Starting handleBooking. ID: ${localBookingId}, Status: ${paymentDetails?.status}`);

    if (!localBookingId) {
      // window.alert("Error: No localBookingId found!");
      toast.error("No active reservation found. Please try again.");
      setCheckoutInitiated(false);
      return;
    }

    try {
      const { data: bookingData, error: bookingError } = await supabase.rpc('confirm_booking', {
        p_booking_id: localBookingId,
        p_payment_intent_id: paymentDetails?.payment_intent_id || null,
        p_payment_status: paymentDetails?.status || null,
        p_is_phone_booking: false
      });

      if (bookingError) {
        console.error("Error confirming booking:", bookingError);
        window.alert(`RPC Error: ${bookingError.message}`);
        toast.error(bookingError.message || "Failed to confirm booking");
        return;
      }

      // Success Handling — attach tier breakdown collected at reservation time
      // window.alert("RPC Success! Processing booking data...");
      const confirmedBooking: Booking = {
        ...(bookingData as Booking),
        ticket_types: pendingTicketDetails ?? null,
      };
      const bookingsArray = [confirmedBooking];
      setCurrentBookings(bookingsArray);
      setPendingTicketDetails(null); // clear after use
      toast.success('Booking successful');

      // QR Code Logic
      if (bookingsArray[0]?.id) {
        try {
          const { data } = await supabase.functions.invoke<{ qr: string, warning?: string }>('issue-qr', {
            body: { booking_id: bookingsArray[0].id, email: session?.user.email }
          })
          if (data?.qr) {
            setIssuedQr(data.qr)
          } else {
            const { data: qrRow } = await supabase
              .from('booking_qr_codes')
              .select('iv_base64, cipher_base64')
              .eq('booking_id', bookingsArray[0].id)
              .maybeSingle()
            const iv = (qrRow as { iv_base64?: string } | null)?.iv_base64
            const ct = (qrRow as { cipher_base64?: string } | null)?.cipher_base64
            if (iv && ct) setIssuedQr(`v1:${iv}:${ct}`)
          }
        } catch { }
      }

      // Refresh Seat Map if needed
      if (seatLayout && selectedScreening) {
        const { data: bookedData } = await supabase
          .rpc('get_booked_seat_ids_for_screening', { p_screening_id: selectedScreening.id });

        // After successful booking, the seats become confirmed.
        // We can just re-run the updated logic or assume they are booked.
        // Re-running logic is safer:
        const booked: string[] = [];
        const held: string[] = [];
        (bookedData || []).forEach((item: any) => {
          const seatId = typeof item === 'string' ? item : item.seat_id;
          const status = typeof item === 'string' ? 'confirmed' : (item.status || 'confirmed');
          if (status === 'confirmed') booked.push(seatId);
          else held.push(seatId);
        });
        setBookedSeats(booked);
        setHeldSeats(held);
        setSelectedSeatIds([]);
      }

      // Clear all pending booking state after successful payment
      // window.alert("Transitioning to completed phase...");
      transitionPhase('completed', 'Payment successful'); // NEW: Transition to completed
      setLocalBookingId(null);
      setHasPendingBooking(false);

    } catch (error: any) {
      console.error("Unexpected error:", error);
      window.alert(`Unexpected HandleBooking Error: ${error.message}`);
      toast.error("An unexpected error occurred");
    } finally {
      setIsProcessing(false);
    }
  };

  if (!show) return null;

  if (showConfirmation && currentBookings.length > 0 && selectedScreening) {
    return (
      <BookingConfirmation
        booking={currentBookings[0]}
        movie={movie}
        screening={selectedScreening}
        userEmail={session?.user?.email || ''}

        qrValue={issuedQr || undefined}
        isLoadingQr={generatingQr}
        onClose={() => {
          setShowConfirmation(false);
          onClose();
        }}
      />
    );
  }

  const clearSeats = () => {
    setSeatCounts({ silver: "", gold: "", diamond: "" });
  };

  return (
    <>
      <div className={styles.modalOverlay}>

        <div className={styles.modalContent}>
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className={styles.modalClose}
            >
              ✕
            </button>
          </div>
          <h2 className={styles.modalTitle}>{movie.title}</h2>

          {/* Warning if user has a pending booking (but NOT if they're continuing it) */}
          {hasPendingBooking && !pendingBookingInfo && (
            <div className="bg-yellow-100 dark:bg-yellow-900 border border-yellow-400 dark:border-yellow-600 text-yellow-800 dark:text-yellow-200 px-4 py-3 rounded-lg mb-4">
              <div className="flex items-start gap-3">
                <span className="text-xl">⚠️</span>
                <div>
                  <p className="font-semibold">You have a pending booking</p>
                  <p className="text-sm mt-1">Please complete or cancel your pending booking before starting a new one. Check "My Bookings" to manage it.</p>
                </div>
              </div>
            </div>
          )}

          {Array.from(new Set(localScreenings.map(s => s.location))).length > 1 && (
            <div className={styles.formGroup}>
              <label className={styles.label}>Select Location</label>
              <select
                className={styles.select}
                value={selectedLocation}
                onChange={(e) => {
                  setSelectedLocation(e.target.value);
                  setSelectedScreening(null);
                }}
              >
                <option value="">Choose a location</option>
                {Array.from(new Set(localScreenings.map(s => s.location))).map((loc) => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>
          )}

          <div className={styles.formGroup}>
            <label className={styles.label}>Select Screening</label>
            <select
              className={styles.select}
              value={selectedScreening?.id || ''}
              onChange={(e) => {
                const screening = localScreenings.find(s => s.id === parseInt(e.target.value));
                setSelectedScreening(screening || null);
                if (screening) {
                  setSelectedLocation(screening.location);
                }
              }}
              disabled={!session || hasPendingBooking}
            >
              <option value="">Choose a time</option>
              {(selectedLocation ? localScreenings.filter(s => s.location === selectedLocation) : localScreenings).map((screening) => (
                <option key={screening.id} value={screening.id}>
                  {format(new Date(screening.start_time), 'PPP p')} - {screening.available_seats} seats available
                </option>
              ))}
            </select>
          </div>

          {selectedScreening && (
            <div className={styles.screeningDetails}>
              <p><strong>Location:</strong> {selectedScreening.location}</p>
              <p><strong>Total Seats:</strong> {selectedScreening.available_seats}</p>
              {selectedScreening.seat_splitting && (
                <div className={styles.detailedSeats}>
                  <p><strong>Total Seats Remaining:</strong> {calculateAvailableSeats(selectedScreening.seat_splitting).total} seats</p>
                  <p><strong>Silver:</strong> {selectedScreening.seat_splitting.silver_total - selectedScreening.seat_splitting.silver_booked} available</p>
                  <p><strong>Gold:</strong> {selectedScreening.seat_splitting.gold_total - selectedScreening.seat_splitting.gold_booked} available</p>
                  <p><strong>Diamond:</strong> {selectedScreening.seat_splitting.diamond_total - selectedScreening.seat_splitting.diamond_booked} available</p>
                </div>
              )}
            </div>
          )}

          {!seatLayout && (
            <SeatSelection
              selectedScreening={selectedScreening}
              seatCounts={seatCounts}
              setSeatCounts={setSeatCounts}
              seatPrices={seatPrices}
              session={session}
              clearSeats={clearSeats}
              splitEnabled={!!selectedScreening?.enable_seatsplitting}
            />
          )}

          {seatLayout && (
            <div className="mt-4 relative">
              {hasPendingBooking && (
                <div className="absolute inset-0 bg-gray-900/70 z-10 flex items-center justify-center rounded-lg backdrop-blur-sm">
                  <div className="text-center p-4">
                    <p className="text-white font-semibold text-lg mb-2">Booking Disabled</p>
                    <p className="text-gray-300 text-sm">Complete or cancel your booking from My Bookings menu</p>
                  </div>
                </div>
              )}
              <BookingLayoutInterface
                layout={{ rows: seatLayout.rows, cols: seatLayout.cols, seats: seatLayout.seats }}
                layoutName={seatLayout.layout_name}
                bookedSeats={bookedSeats}
                heldSeats={heldSeats}
                initialSelectedSeats={pendingBookingInfo?.seatIds || selectedSeatIds}
                onSelectedSeatsChange={(seats) => setSelectedSeatIds(seats)}
                seatNumberingStart={selectedScreening?.seat_numbering_start || 'top-left'}
              />
            </div>
          )}

          {!seatsAvailable && totalTickets > 0 && (
            <div className={styles.errorMessage}>
              The requested seats are not available. Please adjust your selection.
            </div>
          )}

          {totalTickets > 0 && session && !hasPendingBooking && (
            <>
              <div className={styles.formGroup}>
                <label className={styles.label}>Phone Number</label>
                <input
                  type="tel"
                  value={phoneNumber1}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 10) {
                      setPhoneNumber1(value);
                    }
                  }}
                  maxLength={10}
                  className={`${styles.input} ${!phoneNumbersMatch && styles.inputError}`}
                  placeholder="Enter your phone number"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Confirm Your Phone Number</label>
                <input
                  type="tel"
                  value={phoneNumber2}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 10) {
                      setPhoneNumber2(value);
                    }
                  }}
                  maxLength={10}
                  className={`${styles.input} ${!phoneNumbersMatch && styles.inputError}`}
                  placeholder="Confirm your phone number"
                />
              </div>
              {canPhoneBook && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <input
                      type="checkbox"
                      checked={isPhoneBooking}
                      onChange={(e) => setIsPhoneBooking(e.target.checked)}
                      disabled={checkoutInitiated}
                      className="mr-2"
                      title={checkoutInitiated ? "Cannot change booking type after checkout has started" : ""}
                    />
                    Confirm Phone Booking
                    {checkoutInitiated && (
                      <span className="text-xs text-gray-500 ml-2">(locked)</span>
                    )}
                  </label>
                </div>
              )}
              {(canPhoneBook && isPhoneBooking) || (!isPhoneBooking) ? (
                <div className={styles.formGroup}>
                  <label className={styles.label}>Customer Name</label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className={styles.input}
                    placeholder="Enter customer name"
                  />
                </div>
              ) : null}
            </>
          )}

          {session && (
            <TermsAndServices
              acceptedTerms={acceptedTerms}
              setAcceptedTerms={setAcceptedTerms}
              showTerms={showTerms}
              setShowTerms={setShowTerms}

              phoneNumbersMatch={phoneNumbersMatch}
              hasPhoneNumbers={!!phoneNumber1 && !!phoneNumber2}
              organizerTerms={organizerTerms}
            />
          )}

          {canPhoneBook && isPhoneBooking ? (
            <div>
              <div className={styles.priceInfo}>
                <p className={styles.priceText}>Total: €{totalWithFee.toFixed(2)}</p>
              </div>
              <button
                onClick={confirmPhoneBooking}
                disabled={!seatsAvailable || totalTickets === 0}
                className={`${styles.button} ${styles.primaryButton} ${(!seatsAvailable || totalTickets === 0) ? styles.disabledButton : ''}`}
              >
                Confirm Phone Booking
              </button>
            </div>
          ) : (
            <Payment
              totalAmount={totalAmount}
              totalWithFee={totalWithFee}
              acceptedTerms={acceptedTerms}
              checkoutInitiated={checkoutInitiated}
              setCheckoutInitiated={setCheckoutInitiated}
              handleBooking={handleBooking}
              session={session}
              selectedScreening={selectedScreening}
              totalTickets={totalTickets}
              seatPrices={seatPrices}
              setShowAuthModal={setShowAuthModal}
              initiateCheckout={initiateCheckout}
              seatsAvailable={seatsAvailable}
              movieTitle={movie.title}
              selectedSeatIds={selectedSeatIds}
              customerName={customerName}
              phoneNumber={phoneNumber1}
            />
          )}

          <button
            onClick={onClose}
            className={`${styles.button} ${styles.secondaryButton} mt-4`}
          >
            Cancel
          </button>
        </div>
      </div>

      {isProcessing && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex flex-col items-center justify-center">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4"></div>
            <p className="text-lg font-semibold text-gray-800 dark:text-gray-200">Processing your payment...</p>
          </div>
        </div>
      )}

      <AuthModal
        show={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </>
  );
}
